// ============================================================
// ALL IS WELL — LOCATIONS DATA
// Official centre dataset. Do NOT fabricate addresses, phones,
// coordinates, or services. Use COMING SOON for unverified data.
// ============================================================

export interface Centre {
  id: string;
  name: string;
  shortName: string;
  city: string;
  locality?: string;
  type: 'super-clinic' | 'nursing-home' | 'collection-center' | 'help-center';
  address?: string; // Only if verified
  phone?: string;   // Only if verified
  whatsapp?: string; // Only if verified
  timings?: string;  // Only if verified
  latitude?: number; // Only if verified
  longitude?: number; // Only if verified
  services?: string[]; // Only verified services
  departmentIds: string[]; // Departments available at this centre
}

export interface OfficialDepartment {
  id: string;
  name: string;
  officialName: string; // Exact official name — do not rename
  category: string;     // Patient-friendly category id
  description?: string;
}

export interface CareCategory {
  id: string;
  label: string;
  description: string;
  departmentIds: string[]; // Maps to official departments
  icon: string; // Lucide icon name
  color: string;
}

// ============================================================
// 10 OFFICIAL ALL IS WELL CENTRES
// ============================================================
export const centres: Centre[] = [
  {
    id: 'khandwa-super-clinic',
    name: 'All Is Well Super Clinic, Khandwa',
    shortName: 'Khandwa',
    city: 'Khandwa',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'orthopaedics',
      'gynaecology', 'paediatrics', 'general-surgery', 'dental',
    ],
  },
  {
    id: 'burhanpur-super-clinic',
    name: 'All Is Well Super Clinic Burhanpur',
    shortName: 'Burhanpur',
    city: 'Burhanpur',
    type: 'super-clinic',
    departmentIds: [
      'cardiology', 'cardio-thoracic-surgery', 'neuro-spine-surgery',
      'orthopaedics', 'gynaecology', 'paediatrics', 'general-medicine',
      'internal-medicine', 'general-surgery', 'urology', 'ophthalmology',
      'ent', 'dermatology', 'psychiatry', 'radiology', 'dental',
      'nutrition-diet', 'physiotherapy', 'oncology',
    ],
    phone: '+91 7697744444',
    whatsapp: 'https://wa.me/7697744444',
    address: 'Near Macro Vision Academy, Burhanpur, Madhya Pradesh 450331',
    latitude: 21.3144,
    longitude: 76.2294,
  },
  {
    id: 'dharni-super-clinic',
    name: 'All Is Well Super Clinic Dharni',
    shortName: 'Dharni',
    city: 'Dharni',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'gynaecology',
      'paediatrics', 'orthopaedics',
    ],
  },
  {
    id: 'phopnar-collection-center',
    name: 'All Is Well Collection Center Phopnar',
    shortName: 'Phopnar',
    city: 'Phopnar',
    type: 'collection-center',
    departmentIds: ['blood-bank'],
  },
  {
    id: 'khargone-help-center',
    name: 'All Is Well Help Center Khargone',
    shortName: 'Khargone',
    city: 'Khargone',
    type: 'help-center',
    departmentIds: [
      'general-medicine', 'internal-medicine',
    ],
  },
  {
    id: 'shahpur-super-clinic',
    name: 'All Is Well Super Clinic Shahpur',
    shortName: 'Shahpur',
    city: 'Shahpur',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'gynaecology',
      'paediatrics', 'orthopaedics', 'dental',
    ],
  },
  {
    id: 'khaknar-nursing-home',
    name: 'All Is Well Nursing Home Khaknar',
    shortName: 'Khaknar',
    city: 'Khaknar',
    type: 'nursing-home',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'gynaecology', 'paediatrics',
    ],
  },
  {
    id: 'raver-super-clinic',
    name: 'All Is Well Super Clinic, Raver',
    shortName: 'Raver',
    city: 'Raver',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'orthopaedics',
      'gynaecology', 'paediatrics',
    ],
  },
  {
    id: 'sanawad-super-clinic',
    name: 'All Is Well Super Clinic & Pathology, Sanawad',
    shortName: 'Sanawad',
    city: 'Sanawad',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'gynaecology',
      'paediatrics', 'orthopaedics', 'blood-bank',
    ],
  },
  {
    id: 'burhanpur-shani-mandir',
    name: 'All Is Well Super Clinic Shani Mandir, Burhanpur',
    shortName: 'Burhanpur (Shani Mandir)',
    city: 'Burhanpur',
    locality: 'Shani Mandir',
    type: 'super-clinic',
    departmentIds: [
      'general-medicine', 'internal-medicine', 'gynaecology',
      'paediatrics', 'dental',
    ],
  },
];

// ============================================================
// 25 OFFICIAL DEPARTMENTS — MASTER DATASET
// Do NOT rename. Do NOT remove. Do NOT add invented departments.
// ============================================================
export const officialDepartments: OfficialDepartment[] = [
  { id: 'anaesthesia-pain', name: 'Anaesthesia and Pain', officialName: 'Anaesthesia and Pain', category: 'general-health' },
  { id: 'blood-bank', name: 'Blood Bank', officialName: 'Blood Bank', category: 'general-health' },
  { id: 'cardio-thoracic-surgery', name: 'Cardio Thoracic Surgery', officialName: 'Cardio Thoracic Surgery', category: 'heart-chest' },
  { id: 'cardiology', name: 'Cardiology', officialName: 'Cardiology', category: 'heart-chest' },
  { id: 'dental', name: 'Dental', officialName: 'Dental', category: 'dental-health' },
  { id: 'dermatology', name: 'Dermatology', officialName: 'Dermatology', category: 'skin-hair' },
  { id: 'endocrinology', name: 'Endocrinology', officialName: 'Endocrinology', category: 'diabetes-hormonal' },
  { id: 'ent', name: 'ENT', officialName: 'ENT', category: 'ent-hearing' },
  { id: 'gastroenterology', name: 'Gastroenterology', officialName: 'Gastroenterology', category: 'digestive-health' },
  { id: 'general-surgery', name: 'General and Minimal Invasive Surgery', officialName: 'General and Minimal Invasive Surgery', category: 'general-health' },
  { id: 'general-medicine', name: 'General Medicine', officialName: 'General Medicine', category: 'general-health' },
  { id: 'internal-medicine', name: 'Internal Medicine', officialName: 'Internal Medicine', category: 'general-health' },
  { id: 'neuro-spine-surgery', name: 'Neuro And Spine Surgery', officialName: 'Neuro And Spine Surgery', category: 'brain-spine' },
  { id: 'nutrition-diet', name: 'Nutrition and Diet', officialName: 'Nutrition and Diet', category: 'nutrition-wellness' },
  { id: 'gynaecology', name: 'Obstetrics and Gynaecology', officialName: 'Obstetrics and Gynaecology', category: 'womens-health' },
  { id: 'oncology', name: 'Oncology', officialName: 'Oncology', category: 'cancer-care' },
  { id: 'ophthalmology', name: 'Ophthalmology', officialName: 'Ophthalmology', category: 'eye-vision' },
  { id: 'orthopaedics', name: 'Orthopaedics', officialName: 'Orthopaedics', category: 'bones-joints' },
  { id: 'paediatrics', name: 'Paediatrics and Neonatology', officialName: 'Paediatrics and Neonatology', category: 'child-health' },
  { id: 'physiotherapy', name: 'Physiotherapy', officialName: 'Physiotherapy', category: 'bones-joints' },
  { id: 'plastic-surgery', name: 'Plastic And Reconstructive Surgery', officialName: 'Plastic And Reconstructive Surgery', category: 'general-health' },
  { id: 'psychiatry', name: 'Psychiatry', officialName: 'Psychiatry', category: 'mental-wellbeing' },
  { id: 'radiology', name: 'Radiology', officialName: 'Radiology', category: 'general-health' },
  { id: 'rheumatology', name: 'Rheumatology', officialName: 'Rheumatology', category: 'bones-joints' },
  { id: 'urology', name: 'Urology', officialName: 'Urology', category: 'general-health' },
];

// ============================================================
// PATIENT-FRIENDLY CARE CATEGORIES
// Navigation categories only — NOT official departments.
// ============================================================
export const careCategories: CareCategory[] = [
  {
    id: 'heart-chest',
    label: 'HEART & CHEST',
    description: 'Heart conditions, chest pain, cardiovascular health',
    departmentIds: ['cardiology', 'cardio-thoracic-surgery'],
    icon: 'Heart',
    color: 'rose',
  },
  {
    id: 'brain-spine',
    label: 'BRAIN & SPINE',
    description: 'Neurological conditions, spine disorders, headaches',
    departmentIds: ['neuro-spine-surgery'],
    icon: 'Brain',
    color: 'indigo',
  },
  {
    id: 'bones-joints',
    label: 'BONES & JOINTS',
    description: 'Orthopaedic conditions, joint pain, physiotherapy',
    departmentIds: ['orthopaedics', 'physiotherapy', 'rheumatology'],
    icon: 'Bone',
    color: 'slate',
  },
  {
    id: 'womens-health',
    label: "WOMEN'S HEALTH",
    description: 'Obstetrics, gynaecology, pregnancy care',
    departmentIds: ['gynaecology'],
    icon: 'HeartHandshake',
    color: 'pink',
  },
  {
    id: 'child-health',
    label: 'CHILD HEALTH',
    description: 'Paediatrics, neonatology, child development',
    departmentIds: ['paediatrics'],
    icon: 'Baby',
    color: 'emerald',
  },
  {
    id: 'skin-hair',
    label: 'SKIN & HAIR',
    description: 'Dermatology, skin conditions, hair health',
    departmentIds: ['dermatology'],
    icon: 'Sparkles',
    color: 'amber',
  },
  {
    id: 'digestive-health',
    label: 'DIGESTIVE HEALTH',
    description: 'Gastroenterology, digestive disorders',
    departmentIds: ['gastroenterology'],
    icon: 'Activity',
    color: 'orange',
  },
  {
    id: 'diabetes-hormonal',
    label: 'DIABETES & HORMONAL HEALTH',
    description: 'Endocrinology, diabetes, metabolic conditions',
    departmentIds: ['endocrinology', 'general-medicine', 'internal-medicine'],
    icon: 'Droplets',
    color: 'yellow',
  },
  {
    id: 'eye-vision',
    label: 'EYE & VISION',
    description: 'Ophthalmology, cataract, eye care',
    departmentIds: ['ophthalmology'],
    icon: 'Eye',
    color: 'teal',
  },
  {
    id: 'ent-hearing',
    label: 'ENT & HEARING',
    description: 'Ear, nose and throat conditions',
    departmentIds: ['ent'],
    icon: 'Ear',
    color: 'cyan',
  },
  {
    id: 'mental-wellbeing',
    label: 'MENTAL WELLBEING',
    description: 'Psychiatry, mental health support',
    departmentIds: ['psychiatry'],
    icon: 'Brain',
    color: 'violet',
  },
  {
    id: 'dental-health',
    label: 'DENTAL HEALTH',
    description: 'Dental care, oral health',
    departmentIds: ['dental'],
    icon: 'Smile',
    color: 'blue',
  },
  {
    id: 'cancer-care',
    label: 'CANCER CARE',
    description: 'Oncology, cancer evaluation and guidance',
    departmentIds: ['oncology'],
    icon: 'Ribbon',
    color: 'purple',
  },
  {
    id: 'nutrition-wellness',
    label: 'NUTRITION & WELLNESS',
    description: 'Clinical nutrition, diet management',
    departmentIds: ['nutrition-diet'],
    icon: 'Leaf',
    color: 'green',
  },
  {
    id: 'general-health',
    label: 'GENERAL HEALTH',
    description: 'General medicine, internal medicine, other concerns',
    departmentIds: ['general-medicine', 'internal-medicine', 'general-surgery'],
    icon: 'Stethoscope',
    color: 'sky',
  },
];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

export const getCentreById = (id: string): Centre | undefined =>
  centres.find((c) => c.id === id);

export const getDepartmentsByIds = (ids: string[]): OfficialDepartment[] =>
  officialDepartments.filter((d) => ids.includes(d.id));

export const getCentresByCity = (city: string): Centre[] =>
  centres.filter((c) => c.city.toLowerCase() === city.toLowerCase());

export const getDepartmentsForCentre = (centreId: string): OfficialDepartment[] => {
  const centre = getCentreById(centreId);
  if (!centre) return [];
  return getDepartmentsByIds(centre.departmentIds);
};

export const getCareCategoryById = (id: string): CareCategory | undefined =>
  careCategories.find((c) => c.id === id);

export const getDepartmentsForCategory = (categoryId: string): OfficialDepartment[] => {
  const category = getCareCategoryById(categoryId);
  if (!category) return [];
  return officialDepartments.filter((d) => category.departmentIds.includes(d.id));
};

export const isDepartmentAvailableAtCentre = (
  centreId: string,
  departmentId: string
): boolean => {
  const centre = getCentreById(centreId);
  return centre ? centre.departmentIds.includes(departmentId) : false;
};

export const getCentreTypeLabel = (type: Centre['type']): string => {
  const labels: Record<Centre['type'], string> = {
    'super-clinic': 'Super Clinic',
    'nursing-home': 'Nursing Home',
    'collection-center': 'Collection Center',
    'help-center': 'Help Center',
  };
  return labels[type];
};

// Search centres by query (name, city, locality)
export const searchCentres = (query: string): Centre[] => {
  if (!query.trim()) return centres;
  const q = query.toLowerCase();
  return centres.filter(
    (c) =>
      c.name.toLowerCase().includes(q) ||
      c.city.toLowerCase().includes(q) ||
      (c.locality && c.locality.toLowerCase().includes(q)) ||
      c.type.toLowerCase().includes(q)
  );
};
